import {
  Anchor,
  Box,
  Grid,
  Select,
  Stack,
  TextInputProps,
  Input,
  Group,
  Text,
} from '@mantine/core'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { phoneNumberUtil } from 'gga-fe-lib'
import { PhoneNumberItem } from 'gga-fe-lib/dist/lib/phone-number-util'
import { APP_LOCALE } from '@/constants/define'

const CHINA_PHONE_CODE = '86'
const CHINA_SUPPORT_EMAIL = 'gga@geng.gg'

const CHINA_SMS_NOTICE = {
  kr: [
    '죄송합니다. 현재 중국(+86) 휴대폰 번호는 SMS 인증을 지원하지 않습니다.',
    '회원가입 또는 비밀번호 찾기가 필요하신 경우 gga@geng.gg로 문의해 주세요. 확인 후 도움드리겠습니다.',
    '이용에 불편을 드려 죄송합니다.',
  ],
  en: [
    'We’re sorry, but SMS verification is currently unavailable for Chinese (+86) phone numbers.',
    'If you need to sign up or reset your password, please contact us at gga@geng.gg, and we’ll be happy to assist you.',
    'We apologize for the inconvenience.',
  ],
}

const withEmailLink = (line: string) => {
  const [before, after] = line.split(CHINA_SUPPORT_EMAIL)

  if (after === undefined) {
    return line
  }

  return (
    <>
      {before}
      <Anchor href={`mailto:${CHINA_SUPPORT_EMAIL}`} inherit>
        {CHINA_SUPPORT_EMAIL}
      </Anchor>
      {after}
    </>
  )
}

// 회색·dimmed 로 두면 바로 아래 "올바른 국가 코드를 선택해 주세요" 안내와
//   톤이 같아 묻힙니다. 가입 자체가 막힌다는 내용이라 눈에 띄어야 합니다.
const ChinaSmsNotice = () => (
  <Box
    mt={8}
    p={12}
    sx={theme => ({
      border: `1px solid ${theme.colors.yellow[4]}`,
      borderRadius: theme.radius.sm,
      backgroundColor: theme.colors.yellow[0],
    })}
  >
    <Stack spacing={6}>
      {(APP_LOCALE === 'kr' ? CHINA_SMS_NOTICE.kr : CHINA_SMS_NOTICE.en).map(
        line => (
          <Text key={line} size="sm" color="gray.8">
            {withEmailLink(line)}
          </Text>
        )
      )}
    </Stack>
  </Box>
)

type TItemProps = React.ComponentPropsWithoutRef<'div'> & {
  label: string
} & PhoneNumberItem

const SelectItem = forwardRef<HTMLDivElement, TItemProps>(
  (
    { label, flag, countryName, phoneCode, countryCode, ...others }: TItemProps,
    ref
  ) => (
    <div ref={ref} {...others} data-code={countryCode}>
      <Group noWrap>
        <Text size="sm">{`${flag} +${phoneCode} ${countryName}`}</Text>
      </Group>
    </div>
  )
)
SelectItem.displayName = 'SelectItem'

type TPhoneNumberInputProps = {
  inputProps?: Omit<
    TextInputProps,
    'label' | 'value' | 'defaultValue' | 'onChange'
  >
  disabled?: boolean
  disabledCode?: boolean
  disabledNumber?: boolean
  areaCodeOverwrite?: string[]
  label?: React.ReactNode
  value?: TSplitValueType
  placeholder?: string
  defaultValue?: TSplitValueType
  onChange?: (value: TSplitValueType) => void
  [props: string]: any
  hideAreaCode?: boolean
  classes?: any
}

type TSplitValueType = {
  phone_number?: string
  phone_code?: string
  validator?: (num: string) => boolean
}

const PhoneNumberInput = forwardRef<HTMLInputElement, TPhoneNumberInputProps>(
  (props, ref) => {
    const {
      inputProps,
      value,
      defaultValue,
      label,
      onChange,
      disabled,
      disabledCode,
      disabledNumber,
      hideAreaCode = false,
      classes,
      placeholder,
      ...rest
    } = props
    const [selectedCountryCode, setSelectedCountryCode] = useState<
      string | undefined | null
    >(phoneNumberUtil.getCountryCodeByPhoneCode(value?.phone_code || ''))
    const areaCode = useMemo(() => {
      return phoneNumberUtil.getPhoneItems().map(item => {
        return {
          label: `+${item.phoneCode}`,
          value: item.countryCode,
          ...item,
        }
      })
    }, [])

    const _handleChange = useCallback(
      (batchChange: { key: keyof TSplitValueType; val }[]) => {
        if (disabled) {
          return
        }
        const v = {
          ...(value || {}),
        }
        const result = batchChange.reduce((res, chan) => {
          return {
            ...res,
            [chan.key]: chan.val,
          }
        }, v)

        onChange && onChange(result)
      },
      [disabled, onChange, value]
    )

    useEffect(() => {
      if (value?.phone_code && value?.phone_number) {
        try {
          const newCountryCode = phoneNumberUtil.getCountryCodeByPhoneNumber(
            `+${value.phone_code}${value.phone_number}`
          )

          setSelectedCountryCode(newCountryCode)
        } catch (error) {
          // pass
        }
      }
      // const valueCountryCode = value?.phone_code
      //   ? phoneNumberUtil.getCountryCodeByPhoneCode(value.phone_code)
      //   : undefined;
      // if (valueCountryCode) {
      //   setSelectedCountryCode(valueCountryCode);
      // } else {
      //   setSelectedCountryCode(undefined);
      // }
    }, [value.phone_code, value.phone_number])

    return (
      <Input.Wrapper classNames={{ ...classes }} label={label} {...inputProps}>
        <Grid gutter={12}>
          {!hideAreaCode && (
            <Grid.Col span={5}>
              <Select
                searchable={true}
                icon={
                  selectedCountryCode
                    ? phoneNumberUtil.getPhoneItemByCountryCode(
                        selectedCountryCode
                      )?.flag
                    : undefined
                }
                filter={(value, item) => {
                  return (
                    item.phoneCode
                      .toLowerCase()
                      .includes(value.toLowerCase().trim()) ||
                    item.countryName
                      .toLowerCase()
                      .includes(value.toLowerCase().trim())
                  )
                }}
                styles={{
                  root: {
                    position: 'relative',
                  },
                  dropdown: {
                    width: '240px !important',
                    left: '0 !important',
                  },
                }}
                itemComponent={SelectItem}
                disabled={disabled || disabledCode}
                value={selectedCountryCode}
                onChange={v => {
                  setSelectedCountryCode(v)
                  _handleChange([
                    {
                      key: 'phone_code',
                      val: v
                        ? phoneNumberUtil.getPhoneCodeByCountryCode(v)
                        : null,
                    },
                    {
                      key: 'validator',
                      val: v ? phoneNumberUtil.getValidatorByCode(v) : null,
                    },
                  ])
                }}
                data={areaCode}
                error={Boolean(inputProps?.error)}
              />
            </Grid.Col>
          )}
          <Grid.Col span={hideAreaCode ? 12 : 7}>
            <Input
              ref={ref}
              {...rest}
              // icon={value?.phone_code}
              disabled={disabled || disabledNumber || !value?.phone_code}
              value={value?.phone_number}
              onChange={e => {
                _handleChange([
                  { key: 'phone_number', val: e.currentTarget.value },
                ])
              }}
              invalid={Boolean(inputProps?.error)}
              placeholder={placeholder}
            />
          </Grid.Col>
        </Grid>
        {value?.phone_code === CHINA_PHONE_CODE && <ChinaSmsNotice />}
      </Input.Wrapper>
    )
  }
)

PhoneNumberInput.displayName = 'PhoneNumberInput'

export default PhoneNumberInput
